import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { uploadProfilePicture } from '../services/storageService';
import { getCurrentUser, updateUserProfile } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import Button from '../components/Button';

const Title = styled.h1`
  margin-bottom: 10px;
  text-align: center;
`;

const Subtitle = styled.p`
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const ProfilePicturePreview = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: 3px solid #e0e0e0;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #381f74;
    transform: scale(1.05);
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderIcon = styled.div`
  font-size: 48px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 8px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin: 10px 0;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
`;

const SuccessMessage = styled.p`
  color: #2e7d32;
  margin: 10px 0;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 20px;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #666;
  text-decoration: underline;
  cursor: pointer;
  padding: 8px;
  font-family: 'Montserrat', sans-serif;
  
  &:hover {
    color: #333;
  }
`;

const LoadingOverlay = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

interface UploadProfilePictureScreenProps {}

const UploadProfilePictureScreen: React.FC<UploadProfilePictureScreenProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user: authUser, refreshContext, setUser } = useAuth();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if this is a required step (from registration flow) or optional (from profile)
  const isRequired = location.state?.required || true;
  const fromRegistration = location.state?.fromRegistration || false;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, GIF, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    try {
      setUploading(true);
      setError('');
      
      // Use auth context user first, fallback to getCurrentUser if needed
      let currentUser = authUser;
      
      if (!currentUser) {
        console.log("No user in auth context, trying getCurrentUser...");
        currentUser = await getCurrentUser();
      }
      
      if (!currentUser) {
        setError('User not found. Please try logging in again.');
        navigate('/login');
        return;
      }
      
      console.log("User found, proceeding with upload:", currentUser.id);

      // Upload the image
      const downloadURL = await uploadProfilePicture(currentUser.id, selectedFile);
      
      // Update user profile with the new image URL
      await updateUserProfile({
        userId: currentUser.id,
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        profilePictureURL: downloadURL
      });
      
      // Update the auth context with the new profile picture URL
      const updatedUser = {
        ...currentUser,
        profilePictureURL: downloadURL
      };
      setUser(updatedUser);
      
      setSuccess('Profile picture uploaded successfully!');
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard?profileUpdated=true', { replace: true });
      }, 1500);
      
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    if (isRequired && fromRegistration) {
      // For new users, go to dashboard and indicate they skipped
      navigate('/dashboard?skippedProfilePicture=true');
    } else {
      // For existing users, just go back to dashboard
      navigate('/dashboard?skippedProfilePicture=true');
    }
  };

  const handleClickUploadArea = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Upload Profile Picture</title>
        <meta name="description" content={`${APP_NAME} - Upload Profile Picture`} />
      </Helmet>
      
      <AuthLayout imageUrl="/assets/auth-image.jpg" altText="Upload Profile Picture">
        <Title>Add Your Profile Picture</Title>
        <Subtitle>
          {fromRegistration 
            ? "Help others recognize you by adding a profile picture" 
            : "Update your profile picture"
          }
        </Subtitle>

        <UploadContainer>
          <ProfilePicturePreview onClick={handleClickUploadArea}>
            {previewUrl ? (
              <PreviewImage src={previewUrl} alt="Profile preview" />
            ) : (
              <PlaceholderIcon>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <div style={{ fontSize: '12px', fontWeight: '500' }}>
                  Click to upload
                </div>
              </PlaceholderIcon>
            )}
            <LoadingOverlay visible={uploading}>
              Uploading...
            </LoadingOverlay>
          </ProfilePicturePreview>

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <ButtonContainer>
            <Button
              text={uploading ? "Uploading..." : selectedFile ? "Upload Picture" : "Select Picture"}
              onClick={selectedFile ? handleUpload : handleClickUploadArea}
              disabled={uploading}
              loading={uploading}
              width="100%"
            />
            
            {/* {(isRequired && fromRegistration) ? (
              <SkipButton onClick={handleSkip} disabled={uploading}>
                Skip for now
              </SkipButton>
            ) : (
              // <Button
              //   text="Cancel"
              //   onClick={handleSkip}
              //   disabled={uploading}
              //   outline
              //   width="100%"
              // />
              <></>
            )} */}
          </ButtonContainer>
        </UploadContainer>
      </AuthLayout>
    </>
  );
};

export default UploadProfilePictureScreen;
